// Maps the aggregator's unified rows (one shape for both engines) into the
// display view model. The backend sends raw engine status values and a
// display-ready schedule string; the UI owns only status normalization. The
// row is retained on `UnifiedPipeline.raw` so nothing is lost by the mapping.
import {
  EDGE_STATUS_MAP,
  STANDARD_STATUS_MAP,
  UNIFIED_STATUS_DATA_MAP
} from '../../constants/unifiedPipelineStatus';
import {
  UnifiedPipeline,
  UnifiedPipelineEngine,
  UnifiedPipelineNode,
  UnifiedPipelineRaw,
  UnifiedPipelineStatus
} from './interfaces/unifiedPipeline';

const NOT_SCHEDULED = '—';

// ── Status mapping ────────────────────────────────────────────────────────────
// The raw→unified status maps and chip helpers are the SHARED vocabulary (see
// ../../constants/unifiedPipelineStatus). Only the map choice is engine-aware.
export function mapUnifiedStatus(raw: UnifiedPipelineRaw): UnifiedPipelineStatus {
  const map = raw.engine === UnifiedPipelineEngine.EDGE ? EDGE_STATUS_MAP : STANDARD_STATUS_MAP;
  return map[raw.status ?? ''] ?? UnifiedPipelineStatus.IN_PROGRESS;
}

// ── Sync schedule ─────────────────────────────────────────────────────────────
// The backend composes the display message; the UI only strips any stray
// markup (legacy cron messages carried <b> tags) and falls back to "—".
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

export function scheduleDisplay(schedule?: string | null): string {
  const display = schedule ? stripHtml(schedule) : '';
  return display || NOT_SCHEDULED;
}

// ── Displayed ID ──────────────────────────────────────────────────────────────
// Standard rows carry a "-S" suffix ("548-S"); edge rows are shown as their plain
// id ("548", no suffix). This keeps the displayed id unique across engines — a
// standard "548-S" can never collide with an edge "548", and ids are already
// unique within each engine.
export function encodeUnifiedId(engine: UnifiedPipelineEngine, seqId: number): string {
  return engine === UnifiedPipelineEngine.STANDARD ? `${seqId}-S` : `${seqId}`;
}

// ── Row mapper ────────────────────────────────────────────────────────────────
function statusDisplay(status: UnifiedPipelineStatus): string {
  return UNIFIED_STATUS_DATA_MAP[status].displayName;
}

function mapNode(node?: Partial<UnifiedPipelineNode> | null): UnifiedPipelineNode | null {
  if (!node || (!node.type && !node.name)) {
    return null;
  }
  return {
    name: node.name || '',
    type: node.type || '',
    type_display: node.type_display || node.type || '',
    logo_url: node.logo_url || '',
    dark_mode_logo_url: node.dark_mode_logo_url ?? null
  };
}

export function mapUnifiedPipeline(raw: UnifiedPipelineRaw): UnifiedPipeline {
  const status = mapUnifiedStatus(raw);

  return {
    unified_id: encodeUnifiedId(raw.engine, raw.seq_id),
    engine: raw.engine,
    id: raw.id,
    seq_id: raw.seq_id,
    name: raw.name || '',
    source: mapNode(raw.source),
    destination: mapNode(raw.destination),
    status,
    status_display: statusDisplay(status),
    schedule_display: scheduleDisplay(raw.schedule),
    created_ts: raw.created_ts ?? null,
    deleted: raw.deleted ?? null,
    raw
  };
}

export function mapUnifiedPipelines(rows: UnifiedPipelineRaw[]): UnifiedPipeline[] {
  return rows.map(row => mapUnifiedPipeline(row));
}
